import {
  isEmergencyIncident,
  isIncidentEditable,
} from '@collector/shared-ui-domain'
import {
  AttackAttribute,
  DangerousAttackAttribute,
  EventStatusId,
  IncidentId,
  InPossessionAttribute,
  SoccerIncidentId,
} from '@collector/sportsapi-client-legacy'
import { Incident } from '@collector/sportsapi-types'

import { ProbableLinkedIncident } from '../../Shared/RelationDependencies/types'

export function sortProbableLinkedIncidentsByEventTimeAndStatus(
  incidents: ProbableLinkedIncident[],
): void {
  incidents.sort(({ incident: aIncident }, { incident: bIncident }) => {
    const [aHours, aMinutes] = aIncident.event_time?.split(':').map(Number) ?? [
      0, 0,
    ]
    const [bHours, bMinutes] = bIncident.event_time?.split(':').map(Number) ?? [
      0, 0,
    ]

    if (aIncident.event_status_id === bIncident.event_status_id) {
      return bHours !== aHours ? bHours - aHours : bMinutes - aMinutes
    }
    return 1
  })
}

export function sortProbableLinkedIncidentsByOccuredAtField(
  incidents: ProbableLinkedIncident[],
): void {
  const defaultDate = new Date().toDateString()

  incidents.sort(({ incident: aIncident }, { incident: bIncident }) => {
    const aDate = new Date(aIncident.occurred_at || defaultDate)
    const bDate = new Date(bIncident.occurred_at || defaultDate)
    return bDate.getTime() - aDate.getTime()
  })
}

// TODO: it needs to be refactored but in another task
export function filterIncidentsByKeyPassAttribute(
  incidents: ProbableLinkedIncident[],
): ProbableLinkedIncident[] {
  return incidents.filter((incident) => {
    switch (incident.incident.incident_id) {
      case SoccerIncidentId.InPossession:
        return incident.incident.attribute_ids.includes(
          InPossessionAttribute.KeyPass,
        )
      case SoccerIncidentId.Attack:
        return incident.incident.attribute_ids.includes(AttackAttribute.KeyPass)
      case SoccerIncidentId.DangerousAttack:
        return incident.incident.attribute_ids.includes(
          DangerousAttackAttribute.KeyPass,
        )
      default:
        return false
    }
  })
}

export function filterEditableIncidents(
  incidents: ProbableLinkedIncident[],
): ProbableLinkedIncident[] {
  return incidents.filter((incident) =>
    isIncidentEditable(incident.incident as unknown as Incident),
  )
}

export function filterNotEmergencyIncidents(
  incidents: ProbableLinkedIncident[],
): ProbableLinkedIncident[] {
  return incidents.filter(
    (incident) =>
      !isEmergencyIncident(incident.incident as unknown as Incident),
  )
}

export function filterIncidentsWithSameEventTime({
  incidents,
  disabledIncidentsWithSameTime,
  enabledIncidentSequencesWithSameTime,
}: {
  incidents: ProbableLinkedIncident[]
  disabledIncidentsWithSameTime: IncidentId[]
  enabledIncidentSequencesWithSameTime: IncidentId[][]
}): ProbableLinkedIncident[] {
  const groupedIncidents: {
    [key: string]: (ProbableLinkedIncident | ProbableLinkedIncident[])[]
  } = {}

  function saveIncidentInsideGroup(
    eventTime: string,
    eventStatusId: EventStatusId,
    incidentOrIncidents: ProbableLinkedIncident | ProbableLinkedIncident[],
  ): void {
    const key = `${eventTime}-${eventStatusId}`

    if (!groupedIncidents[key]) groupedIncidents[key] = []
    groupedIncidents[key].push(incidentOrIncidents)
  }

  let index = 0

  while (index < incidents.length) {
    const incident = incidents[index]
    const eventTime = incident.incident.event_time
    const eventStatusId = incident.incident.event_status_id

    // Skip incidents without `event_time` or with `00:00` time
    if (!eventTime || eventTime === '00:00') {
      index++
      continue
    }

    const sequence = [incident, incidents[index + 1]].reverse()
    const isSequenceProcessable = enabledIncidentSequencesWithSameTime.some(
      (configSequence) =>
        configSequence.every(
          (incidentId, seqIndex) =>
            sequence[seqIndex]?.incident.incident_id === incidentId,
        ),
    )

    if (isSequenceProcessable) {
      saveIncidentInsideGroup(eventTime, eventStatusId, sequence)
      index += 2
      continue
    }

    if (
      !disabledIncidentsWithSameTime.includes(incident.incident.incident_id)
    ) {
      saveIncidentInsideGroup(eventTime, eventStatusId, incident)
    }

    index++
  }

  return Object.values(groupedIncidents)
    .filter((group) => group.length >= 2)
    .flat()
    .flat()
}
